import { Injectable, EventEmitter } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { map } from "rxjs/operators";
import * as moment from "moment";

@Injectable({
  providedIn: "root",
})
export class CommunityService {
  userJoinedNewCommunity = new EventEmitter<any>();
  userLeftCommunity = new EventEmitter<any>();

  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService
  ) {}

  getCommunityUsers(communityId: number) {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/communities/get-users/${communityId}`
    );
  }

  getPost(postId: number, communityId: number) {
    return this.httpClient.get<any>(
      `${environment.serverPath}/posts/get/${postId}?communityId=${communityId}`
    );
  }

  getAllCommunities() {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/communities/get-communities`
    );
  }

  createCommunity(model: any) {
    return this.httpClient.post<any[]>(
      `${environment.serverPath}/communities/create-community`,
      model
    );
  }

  editCommunity(model: any, id: number) {
    return this.httpClient.put(
      `${environment.serverPath}/communities/create-community/${id}`,
      model
    );
  }

  joinCommunity(model: any, isInvited: boolean = false) {
    return this.httpClient
      .post<any[]>(
        `${environment.serverPath}/communities/join-community`,
        {communityId: model, isInvited}
      )
      .pipe(
        map((x) => {
          this.userJoinedNewCommunity.emit();
          return x;
        })
      );
  }

  getJoinedCommunities(profileId: number) {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/communities/get-joined-communities/${profileId}`
    );
  }

  leaveCommunity(model: any) {
    return this.httpClient
      .post<any[]>(
        `${environment.serverPath}/communities/leave-community`,
        model
      )
      .pipe(
        map((x) => {
          this.userLeftCommunity.emit();
          return x;
        })
      );
  }

  changeCommunityCover(path: string) {
    return this.httpClient.post(
      `${environment.serverPath}/community/update-community-cover`,
      { path }
    );
  }

  getMyWall(page: number = 0, limit: number, communityId: number) {
    return this.httpClient
      .get<{ count: number; items: any[] }>(
        `${environment.serverPath}/posts/community-wall?page=${page}&limit=${limit}&communityId=${communityId}`
      )
      .pipe(
        map((x) => {
          x.items.forEach((o) => {
            this.updateCoverImage(o);
            this.updateProfileImage(o);
            if (o.comments && o.comments.length > 0) {
              o.comments.forEach((z) => {
                this.updateCoverImage(z.profile);
                this.updateProfileImage(z.profile);
              });
            }
          });
          return x;
        })
      );
  }

  updateProfileImage(profile: any) {
    if (!profile.profileImage) {
      profile.profileImage = "/assets/images/profile.jpg";
    } else {
      if (profile.profileImage.indexOf("http") === -1) {
        profile.profileImage = environment.serverPath + profile.profileImage;
      }
    }
  }

  updateCoverImage(profile: any) {
    if (!profile.profileCover) {
      profile.profileCover = "/assets/images/genericCover.png";
    } else {
      if (profile.profileCover.indexOf("http") === -1) {
        profile.profileCover = environment.serverPath + profile.profileCover;
      }
    }
  }

  getPostedOn(datetime: string) {
    const momentTIme = moment(datetime);
    const diffInSeconds = moment().diff(momentTIme, "seconds");
    let str = "";

    if (diffInSeconds < 60) {
      str = this.translateService.instant("timelineHourBeforeSeconds", {
        time: diffInSeconds,
      });
    } else if (diffInSeconds >= 60 && diffInSeconds < 3600) {
      str = this.translateService.instant("timelineHourBeforeMinuts", {
        time: Math.floor(moment().diff(momentTIme, "minutes")),
      });
    } else if (diffInSeconds >= 3600 && diffInSeconds < 86400) {
      str = this.translateService.instant("timelineHourBeforeHours", {
        time: Math.floor(moment().diff(momentTIme, "hours")),
      });
    } else if (diffInSeconds >= 86400 && diffInSeconds < 86400 * 10) {
      str = this.translateService.instant("timelineHourBeforeDays", {
        time: Math.floor(moment().diff(momentTIme, "days")),
      });
    } else {
      str = momentTIme.format("DD/MM/YYYY");
    }
    return str;
  }

  getAllProjects(communityId: number) {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/communities/projects/${communityId}`
    );
  }

  createProject(model: any, communityId: number) {
    return this.httpClient.post(
      `${environment.serverPath}/communities/projects/${communityId}`,
      model
    );
  }

  getProject(projectId: number) {
    return this.httpClient.get<any>(
      `${environment.serverPath}/communities/project/${projectId}`
    );
  }

  getProjectComments(projectId: number) {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/project-comments/${projectId}`
    );
  }

  getProjectComment(projectId: number, commentId: number) {
    return this.httpClient.get<any>(
      `${environment.serverPath}/project-comments/${projectId}/${commentId}`
    );
  }

  createProjectComment(model: any, projectId: number) {
    return this.httpClient.post(
      `${environment.serverPath}/project-comments/${projectId}`,
      model
    );
  }

  deleteComment(commentId: number) {
    return this.httpClient.delete(
      `${environment.serverPath}/project-comments/${commentId}`
    );
  }

  getProjectById(projectId: number) {
    return this.httpClient.get<any>(
      `${environment.serverPath}/communities/projectById/?&projectId=${projectId}`
    );
  }

  editProject(model: any, projectId: number) {
    return this.httpClient.post(
      `${environment.serverPath}/communities/edit-project/${projectId}`,
      model
    );
  }

  getInterests() {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/api/admin/skills/interests`
    );
  }

  getExpertise() {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/api/admin/skills/expertise`
    );
  }  
  sendInvitationTo(model: any) {
    return this.httpClient.post(
      `${environment.serverPath}/communities/send-invitation`,
      model
    );
  }
  sendInvitationToAll(model: any) {
    // return this.httpClient.post(
    //   `${environment.serverPath}/communities/invite-all`,
    //   model
    // );
  }
  isInvited(communityId: number) {
    return this.httpClient.get(
      `${environment.serverPath}/general/isInvited/community/${communityId}`
    );
  }
  deleteCommunity(communityId: number){
    return this.httpClient.delete(
      `${environment.serverPath}/communities/delete-community`,
      { params: { communityId: communityId.toString() } }
    );
  }
  changeCommunityAdmin(model: any) {
      return this.httpClient.put(
        `${environment.serverPath}/communities/change-admin/`,
        model
      );
    }
}
